// The MCP section (UX14/UX15) — everything about how somebody REACHES this gateway
// over MCP, in one place: the token you connect with, and the sessions that token
// opened. They were two nav rows ("Tokens" and "MCP Sessions") describing the two
// halves of one act, which is why the tokens card moved in here.
//
// The route stays /sessions (NOT /mcp — nginx reverse-proxies the /mcp prefix to the
// gateway, so a /mcp SPA route would be proxied away instead of served); /tokens
// redirects here.
//
// The sessions table (H49): a live table of the current /mcp Streamable-HTTP sessions known to the
// gateway — who is connected, under which principal/network, how active, how many tool calls. Admins
// see every session; a normal user sees only their own (server-scoped). The table is the shared
// SortableTable (H40) wrapped in a CollapsibleSection (H42); it seeds with ONE REST fetch then updates
// LIVE from session.opened/session.closed bus frames (no polling), and a row click opens a detail
// Drawer. Empty state when nobody is connected.

import { useEffect, useState } from 'react';
import { api } from '../api/client';
import { useResource } from '../lib/useResource';
import { useAuth } from '../auth/AuthContext';
import { store, useStore, selectSessions, type LiveSession } from '../store/store';
import { Loading, ErrorState, Badge, statusTone } from '../components/ui';
import { DataTable, type Column } from '../components/DataTable';
import { CollapsibleSection } from '../components/CollapsibleSection';
import { Drawer, DetailRow } from '../components/Drawer';
import { McpAccessTokensCard } from './Tokens';

// --- pure helpers (unit-tested in mcpSessions.test.ts) ---------------------

/** Badge tone for a session status. `calling` reads as "busy" (warn); others fall back to statusTone. */
export function mcpSessionTone(status: string) {
  return status === 'calling' ? 'warn' : statusTone(status);
}

/** Human "x ago" for an ISO timestamp, relative to `now` (ms). Empty input → '—'. */
export function relativeSince(iso: string | undefined, now: number = Date.now()): string {
  if (!iso) return '—';
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return '—';
  const s = Math.max(0, Math.round((now - t) / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

/** Roll up a session list into {active, idle, calling, closed, total} counts for the section header.
 * Closed sessions are retained as history (H67) and counted separately from the live statuses. */
export function summarizeSessions(rows: LiveSession[]) {
  const out = { active: 0, idle: 0, calling: 0, closed: 0, total: rows.length };
  for (const r of rows) {
    if (r.closed) out.closed += 1;
    else if (r.status === 'active') out.active += 1;
    else if (r.status === 'idle') out.idle += 1;
    else if (r.status === 'calling') out.calling += 1;
  }
  return out;
}

// --- view ------------------------------------------------------------------

export function McpSessions() {
  const { isAdmin } = useAuth();
  // Seed once from the REST snapshot; thereafter the view is LIVE — session.opened/
  // session.closed frames flow over the event bus into the store and re-render us.
  const res = useResource(() => api.mcpSessions(), []);
  const sessions = useStore(selectSessions);
  const [selected, setSelected] = useState<LiveSession | null>(null);

  // Merge the REST snapshot into the live store map once it loads (upsert, so any
  // frames that arrived before the fetch resolved are preserved).
  useEffect(() => {
    if (res.data) store.seedSessions(res.data.items);
  }, [res.data]);

  const cols: Column<LiveSession>[] = [
    {
      key: 'status',
      header: 'Status',
      sortValue: (s) => (s.closed ? 'zzz-closed' : s.status),
      cell: (s) =>
        s.closed ? (
          <Badge tone="muted">closed</Badge>
        ) : (
          <Badge tone={mcpSessionTone(s.status)}>{s.status}</Badge>
        ),
    },
    {
      key: 'principal',
      header: 'Principal',
      sortValue: (s) => s.principal,
      cell: (s) => <span className="client-title">{s.principal}</span>,
    },
    {
      key: 'networks',
      header: 'Networks',
      sortValue: (s) => s.networks.join(','),
      cell: (s) =>
        s.networks.length ? (
          <span className="client-meta">
            {s.networks.map((n) => (
              <span key={n} className="chip">{n}</span>
            ))}
          </span>
        ) : (
          <span className="muted">—</span>
        ),
    },
    {
      key: 'transport',
      header: 'Transport',
      sortValue: (s) => s.transport,
      cell: (s) => <Badge tone="muted">{s.transport}</Badge>,
    },
    {
      key: 'tool_calls',
      header: 'Tool calls',
      align: 'right',
      sortValue: (s) => s.tool_calls,
      cell: (s) => s.tool_calls,
    },
    {
      key: 'current_tool',
      header: 'Current tool',
      sortValue: (s) => s.current_tool ?? '',
      cell: (s) => (s.current_tool ? <code className="mono small">{s.current_tool}</code> : <span className="muted">—</span>),
    },
    {
      key: 'last_activity',
      header: 'Activity',
      sortValue: (s) => s.last_activity,
      cell: (s) => <span className="muted">{relativeSince(s.last_activity)}</span>,
    },
  ];

  // A gateway predating the endpoint 404s — render an empty state, not an error.
  const unsupported = res.error != null && /\b404\b/.test(res.error);
  // Render from the LIVE store map (seeded from REST, kept current by the bus).
  const rows = Object.values(sessions);
  const summary = summarizeSessions(rows);

  return (
    <div className="view">
      <div className="view-head">
        <h1>MCP</h1>
      </div>

      {/* UX15 — the token you connect WITH, above the sessions it opened. Every
          endpoint behind this card is scoped to the caller server-side, so it is the
          same card for a member and an admin. */}
      <McpAccessTokensCard />

      <p className="small muted">
        Live <code>/mcp</code> Streamable-HTTP sessions{isAdmin ? ' across the org' : ' you own'} — updates live as clients connect and disconnect; recently-closed sessions are retained so you can inspect them.
      </p>

      {res.loading && !res.data ? (
        <Loading what="Loading sessions…" />
      ) : res.error && !unsupported ? (
        <ErrorState message={res.error} onRetry={res.reload} />
      ) : (
        <CollapsibleSection
          title={
            <span>
              Sessions{' '}
              <span className="muted small">
                ({summary.active} active · {summary.calling} calling · {summary.idle} idle · {summary.closed} closed)
              </span>
            </span>
          }
          storageKey="mcpgraph.mcpSessions.current"
        >
          <DataTable
            columns={cols}
            rows={rows}
            rowKey={(s) => s.session_id}
            onRowClick={(s) => setSelected(s)}
            initialSort={{ key: 'last_activity', dir: 'desc' }}
            empty="No MCP sessions yet — connect an MCP client to /mcp with a token."
          />
        </CollapsibleSection>
      )}

      {selected && <SessionDrawer session={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}

function SessionDrawer({ session, onClose }: { session: LiveSession; onClose: () => void }) {
  return (
    <Drawer
      title={
        <span className="client-title">
          {session.principal}{' '}
          {session.closed ? (
            <Badge tone="muted">closed</Badge>
          ) : (
            <Badge tone={mcpSessionTone(session.status)}>{session.status}</Badge>
          )}
        </span>
      }
      subtitle={<code className="mono small">{session.session_id}</code>}
      onClose={onClose}
    >
      {session.closed && (
        <DetailRow label="Closed">
          {session.closed_ts ? `${new Date(session.closed_ts).toLocaleString()} (${relativeSince(new Date(session.closed_ts).toISOString())})` : 'yes'}
        </DetailRow>
      )}
      <DetailRow label="Principal">{session.principal}</DetailRow>
      <DetailRow label="User id"><code className="mono small">{session.user_id}</code></DetailRow>
      <DetailRow label="Grant id"><code className="mono small">{session.grant_id}</code></DetailRow>
      <DetailRow label="Source">{session.source}</DetailRow>
      <DetailRow label="Transport">{session.transport}</DetailRow>
      <DetailRow label="Networks">
        <span className="client-meta">
          {session.networks.length ? session.networks.map((n) => <span key={n} className="chip">{n}</span>) : '—'}
        </span>
      </DetailRow>
      <DetailRow label="Tool calls">{session.tool_calls}</DetailRow>
      <DetailRow label="Current tool">{session.current_tool ? <code className="mono small">{session.current_tool}</code> : '—'}</DetailRow>
      <DetailRow label="Last method">{session.last_method ?? '—'}</DetailRow>
      <DetailRow label="Started">{new Date(session.started_at).toLocaleString()} ({relativeSince(session.started_at)})</DetailRow>
      <DetailRow label="Last activity">{new Date(session.last_activity).toLocaleString()} ({relativeSince(session.last_activity)})</DetailRow>
    </Drawer>
  );
}
